import React, { useCallback, useState, useEffect, useRef } from 'react'
import { Unity, useUnityContext } from 'react-unity-webgl'
import { ReactUnityEventParameter } from 'react-unity-webgl/distribution/types/react-unity-event-parameters'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { logger } from '../../utils'
import { KeyboardControl } from '../molecules/KeyboardControls'
import { LoadingSpinner } from '../atoms'

interface ComponentPosition {
  id: string
  width: number
  height: number
  x: number
  y: number
  dpi: number
  screenWidth: number
  screenHeight: number
  aspectRatio: number
  // Added raw pixel values for more precise positioning
  rawX: number
  rawY: number
  rawWidth: number
  rawHeight: number
  osScalingFactor: number
}

export interface TrackedComponent {
  id: string
  ref: React.RefObject<HTMLElement>
}

export enum FocusableElement {
  CanvasUnity = 'canvas-unity',
}

interface UnityPlayerProps {
  onUpdateXP: (json: string) => void
  onEndGame: (json: string) => void
  onNewLightningModule: (level: string) => void
  payloadToUnity: any
  trackedComponents?: TrackedComponent[]
  onMcqSubmit?: () => void
  onMcqReset?: () => void
  onMcqOptionSelected?: () => void
  onMcqWrongAnswer?: () => void
};

// NOTE: This will likely be cleaned up with DMVP-681, but keeping for now, as removing this may cause issues which would be better addressed in a separate ticket.
/**
 * Helper function to detect OS-level scaling
 * This attempts to detect if the operating system is applying
 * additional scaling to the display
 */
const detectOSScaling = (): number => {
  // Check for Windows DPI scaling
  // Create a test element to measure physical vs logical pixels
  const testDiv = document.createElement('div')
  testDiv.style.width = '1in'
  testDiv.style.height = '1in'
  testDiv.style.position = 'absolute'
  testDiv.style.left = '-100%'
  testDiv.style.top = '-100%'
  document.body.appendChild(testDiv)

  // Get the physical size in device pixels
  const devicePixelWidth = testDiv.offsetWidth * window.devicePixelRatio

  // Standard DPI is 96, so 1 inch should be 96 pixels
  // If we see significant deviation, OS scaling is likely in effect
  const osScaling = devicePixelWidth / 96

  // Clean up
  document.body.removeChild(testDiv)
  return osScaling
}

const UnityPlayer: React.FC<UnityPlayerProps> = ({ onUpdateXP, onEndGame, onNewLightningModule, payloadToUnity, trackedComponents = [], onMcqSubmit, onMcqReset, onMcqOptionSelected, onMcqWrongAnswer }: UnityPlayerProps) => {
  // Device metrics
  const [devicePixelRatio, setDevicePixelRatio] = useState(window.devicePixelRatio)
  const [osScalingFactor, setOSScalingFactor] = useState(1)
  const [gameEnded, setGameEnded] = useState(false)
  const [unityReady, setUnityReady] = useState(false)
  const keyboardControls = useKeyboardControls()
  const unityBuildVersion = 'V32'

  // Reference to track whether positions were sent to Unity
  const positionsSentRef = useRef(false)
  const resizeTimeoutRef = useRef<number | null>(null)
  const positionUpdateCountRef = useRef(0)

  // New refs to prevent state updates during resize
  const devicePixelRatioRef = useRef(window.devicePixelRatio)
  const osScalingFactorRef = useRef(1)
  const isResizingRef = useRef(false)

  const {
    isLoaded,
    unityProvider,
    addEventListener,
    removeEventListener,
    unload,
    sendMessage,
    UNSAFE__detachAndUnloadImmediate: detachAndUnloadImmediate
  } = useUnityContext({
    loaderUrl: `/assets/unity/UnityBuilds_${unityBuildVersion}.loader.js`,
    dataUrl: `/assets/unity/UnityBuilds_${unityBuildVersion}.data`,
    frameworkUrl: `/assets/unity/UnityBuilds_${unityBuildVersion}.framework.js`,
    codeUrl: `/assets/unity/UnityBuilds_${unityBuildVersion}.wasm`
  })

  // Detect OS scaling on component mount
  useEffect(() => {
    const scaling = detectOSScaling()
    setOSScalingFactor(scaling)
    osScalingFactorRef.current = scaling
  }, [])

  // Mark Unity as ready when loaded
  useEffect(() => {
    if (isLoaded && !unityReady) {
      const timer = setTimeout(() => {
        setUnityReady(true)
      }, 1000) // Give Unity a second to initialize fully

      return () => clearTimeout(timer)
    }
  }, [isLoaded, unityReady])

  // New function to send positions without triggering state updates
  // Used during active resize to prevent infinite loop
  const sendPositionsWithoutStateUpdates = useCallback(() => {
    if (!isLoaded || !unityReady) return

    const componentPositions: ComponentPosition[] = []
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight
    const aspectRatio = screenWidth / screenHeight
    const dpi = devicePixelRatioRef.current * 96 // Approximate DPI calculation

    trackedComponents.forEach(component => {
      if (component.ref.current != null) {
        const rect = component.ref.current.getBoundingClientRect()

        // Get element's position relative to viewport
        const elementLeft = rect.left
        const elementTop = rect.top
        const elementWidth = rect.width
        const elementHeight = rect.height

        // Calculate normalized positions (0-1 range)
        const normalizedX = elementLeft / screenWidth
        const normalizedY = elementTop / screenHeight
        const normalizedWidth = elementWidth / screenWidth
        const normalizedHeight = elementHeight / screenHeight

        // Include both normalized and raw pixel values
        componentPositions.push({
          id: component.id,
          // Normalized values (0-1)
          width: normalizedWidth,
          height: normalizedHeight,
          x: normalizedX,
          y: normalizedY,
          // Raw pixel values
          rawX: elementLeft,
          rawY: elementTop,
          rawWidth: elementWidth,
          rawHeight: elementHeight,
          // Device metrics
          dpi,
          screenWidth,
          screenHeight,
          aspectRatio,
          osScalingFactor: osScalingFactorRef.current
        })
      }
    })

    if (componentPositions.length > 0) {
      sendMessage('ReactCommunicator', 'UpdateComponentPositions', JSON.stringify(componentPositions))
    }
  }, [isLoaded, trackedComponents, sendMessage, unityReady])

  // Function to get component positions and send to Unity
  const sendComponentPositionsToUnity = useCallback(() => {
    if (!isLoaded || !unityReady) return

    // Skip additional state updates during active resize
    if (isResizingRef.current) {
      sendPositionsWithoutStateUpdates()
      return
    }

    const componentPositions: ComponentPosition[] = []
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight
    const aspectRatio = screenWidth / screenHeight
    const dpi = window.devicePixelRatio * 96 // Approximate DPI calculation

    positionUpdateCountRef.current += 1

    trackedComponents.forEach(component => {
      if (component.ref.current != null) {
        const rect = component.ref.current.getBoundingClientRect()

        // Get element's position relative to viewport
        const elementLeft = rect.left
        const elementTop = rect.top
        const elementWidth = rect.width
        const elementHeight = rect.height

        // Calculate normalized positions (0-1 range)
        const normalizedX = elementLeft / screenWidth
        const normalizedY = elementTop / screenHeight
        const normalizedWidth = elementWidth / screenWidth
        const normalizedHeight = elementHeight / screenHeight

        // Include both normalized and raw pixel values
        componentPositions.push({
          id: component.id,
          // Normalized values (0-1)
          width: normalizedWidth,
          height: normalizedHeight,
          x: normalizedX,
          y: normalizedY,
          // Raw pixel values
          rawX: elementLeft,
          rawY: elementTop,
          rawWidth: elementWidth,
          rawHeight: elementHeight,
          // Device metrics
          dpi,
          screenWidth,
          screenHeight,
          aspectRatio,
          osScalingFactor
        })
      }
    })

    if (componentPositions.length > 0) {
      sendMessage('ReactCommunicator', 'UpdateComponentPositions', JSON.stringify(componentPositions))
      positionsSentRef.current = true
    }
  }, [isLoaded, trackedComponents, sendMessage, unityReady, osScalingFactor, sendPositionsWithoutStateUpdates])

  useEffect(() => {
    const w = window as any

    w.Unity_RequestPositionUpdate = () => {
      if (isLoaded && unityReady && trackedComponents.length > 0) {
        sendComponentPositionsToUnity()
      }
    }

    // Set up accessibility update function for real-time updates
    w.Unity_UpdateAccessibility = (reduceMotion: boolean) => {
      if (isLoaded && unityReady) {
        logger.log(`🎯 UnityPlayer: Sending reduce motion update to Unity: ${String(reduceMotion)}`)
        try {
          sendMessage('GAME_CONTROLLER', 'SetReduceMotion', reduceMotion.toString())
          logger.log('✅ UnityPlayer: Successfully sent accessibility update to GAME_CONTROLLER')
        } catch (error) {
          logger.error('❌ UnityPlayer: Failed to send accessibility update:')
          logger.error(error as any)
        }
      } else {
        logger.log('❌ UnityPlayer: Unity not ready, cannot send accessibility update')
      }
    }

    // Clean up when component unmounts
    return () => {
      w.Unity_RequestPositionUpdate = undefined
      w.Unity_UpdateAccessibility = undefined
    }
  }, [isLoaded, unityReady, trackedComponents, sendComponentPositionsToUnity, sendMessage])

  // Handle window resize with debouncing and multiple updates
  const handleResize = useCallback(() => {
    // Mark that we're in an active resize
    isResizingRef.current = true

    // Update ref values (no re-render)
    devicePixelRatioRef.current = window.devicePixelRatio

    // Clear any existing timeout
    if (resizeTimeoutRef.current !== null) {
      window.clearTimeout(resizeTimeoutRef.current)
    }

    // Send an initial update quickly using the no-state-update function
    if (trackedComponents.length > 0 && unityReady) {
      sendPositionsWithoutStateUpdates()
    }

    // Set a new timeout to update positions after resize ends
    resizeTimeoutRef.current = window.setTimeout(() => {
      // Resize is finished - update state and trigger normal render cycle
      isResizingRef.current = false
      setDevicePixelRatio(window.devicePixelRatio)

      if (trackedComponents.length > 0 && unityReady) {
        // Final update with the normal function
        sendComponentPositionsToUnity()
        resizeTimeoutRef.current = null
      }
    }, 150) // Wait for resize to finish before updating
  }, [trackedComponents, sendComponentPositionsToUnity, unityReady, sendPositionsWithoutStateUpdates])

  // Send component positions to Unity when everything is ready
  useEffect(() => {
    if (isLoaded && unityReady && trackedComponents.length > 0 && !positionsSentRef.current) {
      // Initial delay to ensure both Unity and React components are fully rendered
      const timer = setTimeout(() => {
        sendComponentPositionsToUnity()

        // Only set up the second timer if we haven't unmounted
        return () => clearTimeout(timer)
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [isLoaded, unityReady, trackedComponents.length, sendComponentPositionsToUnity])

  // Monitor for device pixel ratio changes
  useEffect(() => {
    // Function to update device pixel ratio
    const updateDevicePixelRatio = (): void => {
      // Update refs first to avoid re-renders during processing
      devicePixelRatioRef.current = window.devicePixelRatio

      // Recalculate OS scaling after DPR changes
      const scaling = detectOSScaling()
      osScalingFactorRef.current = scaling

      // Finally update state (triggers render)
      setDevicePixelRatio(window.devicePixelRatio)
      setOSScalingFactor(scaling)

      // Trigger position update
      if (unityReady) {
        setTimeout(() => sendComponentPositionsToUnity(), 100)
      }
    }

    // Media matcher for device pixel ratio changes
    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${devicePixelRatio}dppx)`
    )

    // Add event listeners
    mediaMatcher.addEventListener('change', updateDevicePixelRatio)
    window.addEventListener('resize', handleResize)

    return () => {
      mediaMatcher.removeEventListener('change', updateDevicePixelRatio)
      window.removeEventListener('resize', handleResize)
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [devicePixelRatio, handleResize, sendComponentPositionsToUnity, unityReady])

  // Clean up Unity when component unmounts
  useEffect(() => {
    if (isLoaded) {
      return () => {
        detachAndUnloadImmediate().catch((reason) => {
          logger.log(reason)
        })
      }
    }
  }, [detachAndUnloadImmediate, isLoaded])

  // Handle game ending
  useEffect(() => {
    if (gameEnded) {
      void unload()
    }
  }, [gameEnded, unload])

  // Callback handlers for Unity events
  const handleUpdateXP = useCallback((...parameters: ReactUnityEventParameter[]): void => {
    const json = parameters[0] as string
    onUpdateXP(json)
  }, [onUpdateXP])

  const handleNewLightningModule = useCallback((...parameters: ReactUnityEventParameter[]): void => {
    const json = parameters[0] as string
    onNewLightningModule(json)
  }, [onNewLightningModule])

  const handleEndGame = useCallback((...parameters: ReactUnityEventParameter[]): void => {
    setGameEnded(true)
    const json = parameters[0] as string
    onEndGame(json)
    keyboardControls.focusNextElement()
  }, [onEndGame, keyboardControls])

  const handleReleaseBrowserFocusForward = useCallback(() => {
    keyboardControls.focusNextElement()
  }, [keyboardControls])

  const handleReleaseBrowserFocusBack = useCallback(() => {
    keyboardControls.focusPreviousElement()
  }, [keyboardControls])

  const handleMcqSubmit = useCallback((): void => { onMcqSubmit?.() }, [onMcqSubmit])
  const handleMcqReset = useCallback((): void => { onMcqReset?.() }, [onMcqReset])
  const handleMcqOptionSelected = useCallback((): void => { onMcqOptionSelected?.() }, [onMcqOptionSelected])
  const handleMcqWrongAnswer = useCallback((): void => { onMcqWrongAnswer?.() }, [onMcqWrongAnswer])

  // Callback for Unity initialization
  const onDataProviderInitialized = useCallback((): void => {
    sendMessage('ExternalData', 'ReceiveExternalData', JSON.stringify(payloadToUnity))

    // Tell Unity we're ready to start communicating
    setUnityReady(true)

    // Send initial positions after a slight delay
    setTimeout(() => {
      sendComponentPositionsToUnity()
    }, 500)
  }, [sendMessage, payloadToUnity, sendComponentPositionsToUnity])

  // Send external data to Unity
  useEffect(() => {
    if (isLoaded) {
      sendMessage('ExternalData', 'ReceiveExternalData', JSON.stringify(payloadToUnity))
    }
  }, [isLoaded, payloadToUnity, sendMessage])

  // Add and remove event listeners
  useEffect(() => {
    if (isLoaded) {
      addEventListener('DataProviderInitialized', onDataProviderInitialized)
      addEventListener('UpdateXP', handleUpdateXP)
      addEventListener('EndGame', handleEndGame)
      addEventListener('NewLightningModule', handleNewLightningModule)
      addEventListener('ReleaseBrowserFocusForward', handleReleaseBrowserFocusForward)
      addEventListener('ReleaseBrowserFocusBack', handleReleaseBrowserFocusBack)
      addEventListener('McqSubmit', handleMcqSubmit)
      addEventListener('McqReset', handleMcqReset)
      addEventListener('McqOptionSelected', handleMcqOptionSelected)
      addEventListener('McqWrongAnswer', handleMcqWrongAnswer)
    }

    return () => {
      if (isLoaded) {
        removeEventListener('DataProviderInitialized', onDataProviderInitialized)
        removeEventListener('UpdateXP', handleUpdateXP)
        removeEventListener('EndGame', handleEndGame)
        removeEventListener('NewLightningModule', handleNewLightningModule)
        removeEventListener('ReleaseBrowserFocusForward', handleReleaseBrowserFocusForward)
        removeEventListener('ReleaseBrowserFocusBack', handleReleaseBrowserFocusBack)
        removeEventListener('McqSubmit', handleMcqSubmit)
        removeEventListener('McqReset', handleMcqReset)
        removeEventListener('McqOptionSelected', handleMcqOptionSelected)
        removeEventListener('McqWrongAnswer', handleMcqWrongAnswer)
      }
    }
  }, [addEventListener, isLoaded, removeEventListener, handleUpdateXP, handleEndGame, onDataProviderInitialized, handleNewLightningModule, handleReleaseBrowserFocusForward, handleReleaseBrowserFocusBack, handleMcqSubmit, handleMcqReset, handleMcqOptionSelected, handleMcqWrongAnswer])

  // Handle keyboard focus
  const onFocusEnterFromStart = useCallback((): void => {
    sendMessage('ElementManager', 'RequestFocusFirstElement')
  }, [sendMessage])

  const onFocusEnterFromEnd = useCallback((): void => {
    sendMessage('ElementManager', 'RequestFocusLastElement')
  }, [sendMessage])

  // Render both Unity and the loading spinner together
  // The spinner is shown on top while Unity loads in the background
  const renderUnityPlayer = (): JSX.Element => {
    return (
      <div
        data-testid='unity-canvas-wrapper'
        className='unityplayer_fullscreen'
      >
        <KeyboardControl
          isExternal
          manualFocusHandling
          onFocusEnterFromStart={onFocusEnterFromStart}
          onFocusEnterFromEnd={onFocusEnterFromEnd}
        >
          <Unity
            id={FocusableElement.CanvasUnity}
            tabIndex={-1}
            unityProvider={unityProvider}
            style={{ width: '100%', height: '100%' }}
            devicePixelRatio={1}
          />
        </KeyboardControl>
      </div>
    )
  }

  return (
    <div className='relative w-full h-full'>
      {/* Always render Unity component, but it may be invisible */}
      {renderUnityPlayer()}

      {/* Show the loading spinner on top while Unity loads */}
      {!isLoaded && (
        <div className='absolute inset-0 h-full w-full flex justify-center items-center'>
          <LoadingSpinner className='w-1/4 min-w-[80px] max-w-[200px]' />
        </div>
      )}
    </div>
  )
}

export default UnityPlayer
